import { useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { IconX } from '@shared/ui/icons';
import { AssistantChat } from './AssistantChat';
import { useActiveAssistantSession } from './useActiveAssistantSession';

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.z.overlay};
  width: min(460px, 100vw);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadow.lg};
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  h2 {
    flex: 1;
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
`;

const Close = styled.button`
  display: inline-flex;
  padding: ${({ theme }) => theme.spacing(1)};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const ChatRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};

  select {
    flex: 1;
    min-width: 0;
  }
`;

const HintRow = styled(ChatRow)`
  align-items: center;
  justify-content: space-between;
`;

const HintText = styled.span`
  font-size: 11.5px;
  opacity: 0.72;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
`;

/**
 * Помощник поверх текущей страницы: свои чаты (выбор, «Новый чат»),
 * переход на весь экран. Вопрос уходит с адресом страницы, чтобы
 * Помощник понимал, о каком разделе речь.
 */
export function AssistantDrawer({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { active, setActive, sessions } = useActiveAssistantSession();
  const items = sessions.data?.items ?? [];

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Panel aria-label="Помощник">
      <Head>
        <TitleRow>
          <h2>Помощник</h2>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => {
              onClose();
              navigate('/assistant');
            }}
          >
            На весь экран
          </Button>
          <Close type="button" onClick={onClose} aria-label="Закрыть Помощника" title="Закрыть">
            <IconX size={18} />
          </Close>
        </TitleRow>
        <ChatRow>
          <Select
            value={active ?? ''}
            onChange={(e) => setActive(e.target.value || null)}
            aria-label="Чат"
          >
            <option value="">Новый чат</option>
            {items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </Select>
          <Button type="button" $variant="secondary" $size="sm" onClick={() => setActive(null)}>
            + Новый чат
          </Button>
        </ChatRow>
        <HintRow>
          <HintText>
            Справка платформы и открытые регламенты (ЛНА)
          </HintText>
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => {
              onClose();
              navigate('/requests');
            }}
          >
            Заявка службе / юристам →
          </Button>
        </HintRow>
      </Head>
      <Body>
        <AssistantChat sessionId={active} onSessionChange={setActive} page={pathname} />
      </Body>
    </Panel>
  );
}
